import { TILE } from './constants';
import { dist2 } from '../lib/intmath';
import { unitDef } from '../content/units';
import { buildingDef } from '../content/buildings';
import { UnitMode } from './types';
import type { Building, UnitId } from './types';
import type { CombatUnit } from './combat';
import type { Grid } from './path';

/*
 * מחזור הכרייה.
 *
 * הקוצר נוהג בעצמו — אין פקודת שחקן לכל צעד. זו "בינה" בתוך הסימולציה, ולכן
 * היא חייבת להיות דטרמיניסטית לחלוטין: כל שבירת שוויון נעשית לפי מרחק ואז לפי
 * אינדקס/מזהה, לעולם לא לפי סדר במערך.
 *
 * המחזור: מוצא שדה → נוסע → כורה עד שהוא מלא או שהשדה נגמר → מוצא מפעל עיבוד →
 * נוסע → פורק ומזכה באשראי → חוזר להתחלה.
 *
 * **העפרות באמת נגמרות מהאריח.** שכבת העפרות היא חלק מהמצב, נכנסת ל-hash ולשמירה,
 * והרנדרר מצייר את דרגת העושר שלה — כך ששדה מדולדל גם נראה מדולדל.
 */

/** כמה אשראי מקבלים על יחידת עפרות אחת. */
export const ORE_VALUE = 1;

/**
 * כל כמה טיקים העפרות צומחות. 100 טיקים = חמש שניות.
 *
 * **הכלכלה חייבת להיות ברז ולא דלי.** שדה שנגמר לתמיד הופך כל משחק ארוך למרוץ
 * שעון: מי שסיים לכרות ראשון מנצח, ואחריו אין מה לעשות. ב-RA2 העפרות גדלות
 * מחדש, וזו הסיבה שהמפה נשארת שווה קרב גם בדקה העשרים.
 */
export const ORE_GROWTH_TICKS = 100;

/** כמה נוסף לאריח בכל מחזור צמיחה. */
export const ORE_GROWTH_AMOUNT = 45;

/**
 * מצמיחה את שכבת העפרות בחזרה לעבר מה שהמפה מגדירה.
 *
 * **התקרה היא המפה עצמה, ולא מספר.** מכאן שתי תכונות שאי אפשר לקבל בלי זה:
 * אריח שנכרה עד אפס **כן** חוזר לצמוח, כי המפה עדיין אומרת שיש שם שדה; ושדה
 * לעולם לא מתפשט אל מעבר לצורתו המקורית, כלומר האיזון של המפה נשמר.
 *
 * דטרמיניסטי לחלוטין: התנאי תלוי בטיק בלבד, והצמיחה בשכבת הבסיס בלבד.
 */
export function growOre(tick: number, baseline: Uint16Array, access: OreAccess): boolean {
    if (tick % ORE_GROWTH_TICKS !== 0) return false;
    const current = access.read();
    let grown = false;
    for (let i = 0; i < baseline.length; i++) {
        const max = baseline[i] ?? 0;
        if (max === 0) continue;
        const now = current[i] ?? 0;
        if (now >= max) continue;
        if (!grown) {
            grown = true;
            access.write();
        }
        access.read()[i] = Math.min(max, now + ORE_GROWTH_AMOUNT);
    }
    return grown;
}

/** עד כמה רחוק קוצר מוכן לחפש שדה או מפעל, ביחידות עולם. */
const SEARCH_RADIUS = 40 * TILE;

export function isHarvester(unit: CombatUnit): boolean {
    return unitDef(unit.type).capacity > 0;
}

function tileOf(unit: CombatUnit, grid: Grid): number {
    return Math.floor(unit.y / TILE) * grid.w + Math.floor(unit.x / TILE);
}

/**
 * האריח עם העפרות הקרוב ביותר. שבירת שוויון לפי אינדקס האריח — שני מחשבים
 * שמריצים את אותו משחק חייבים לבחור את אותו אריח.
 */
export function nearestOreTile(
    unit: CombatUnit,
    ore: Uint16Array,
    grid: Grid,
): number {
    let best = -1;
    let bestDistance = Number.MAX_SAFE_INTEGER;
    const limit = SEARCH_RADIUS * SEARCH_RADIUS;

    for (let i = 0; i < ore.length; i++) {
        if ((ore[i] ?? 0) === 0) continue;
        const tx = i % grid.w;
        const ty = (i - tx) / grid.w;
        const d2 = dist2(unit.x, unit.y, tx * TILE + TILE / 2, ty * TILE + TILE / 2);
        if (d2 > limit) continue;
        if (d2 < bestDistance || (d2 === bestDistance && i < best)) {
            bestDistance = d2;
            best = i;
        }
    }
    return best;
}

/** מפעל העיבוד הקרוב ביותר של אותו שחקן. שבירת שוויון לפי מזהה. */
export function nearestRefinery(
    unit: CombatUnit,
    buildings: readonly Building[],
): Building | null {
    let best: Building | null = null;
    let bestDistance = Number.MAX_SAFE_INTEGER;

    for (const building of buildings) {
        if (building.owner !== unit.owner || building.hp <= 0) continue;
        if (!buildingDef(building.type).acceptsOre) continue;
        const center = buildingCenter(building);
        const d2 = dist2(unit.x, unit.y, center.x, center.y);
        if (d2 < bestDistance || (d2 === bestDistance && (best === null || building.id < best.id))) {
            bestDistance = d2;
            best = building;
        }
    }
    return best;
}

export function buildingCenter(building: Building): { x: number; y: number } {
    const def = buildingDef(building.type);
    return {
        x: building.tileX * TILE + (def.w * TILE) / 2,
        y: building.tileY * TILE + (def.h * TILE) / 2,
    };
}

/** האם היחידה נמצאת מספיק קרוב למבנה כדי לפרוק. */
export function atBuilding(unit: CombatUnit, building: Building): boolean {
    const def = buildingDef(building.type);
    const center = buildingCenter(building);
    // רדיוס נדיב: מספיק להגיע לשולי המבנה, לא למרכזו המדויק.
    const reach = ((Math.max(def.w, def.h) + 1) * TILE) / 2;
    return dist2(unit.x, unit.y, center.x, center.y) <= reach * reach;
}

export interface HarvestOutcome {
    /** אשראי שנוסף לשחקן בטיק הזה. */
    readonly credits: number;
    /** האם שכבת העפרות שונתה — הקורא מעתיק אותה פעם אחת אם כן. */
    readonly oreChanged: boolean;
}

/**
 * גישה לשכבת העפרות בהעתקה-בכתיבה.
 *
 * **זו לא אופטימיזציה מוקדמת — זה תיקון לבאג שנמדד.** הקורא העתיק את המערך
 * לפני הקריאה, ולכן די היה בקוצר חי אחד כדי להעתיק 9,216 ערכים בכל טיק, תמיד.
 * המפה המוקטנת מזהה שינוי לפי **הפניה**, ולכן היא ציירה את עצמה מחדש עשרים
 * פעם בשנייה. `write()` נקראת רק ברגע שבאמת כורים.
 */
export interface OreAccess {
    /** השכבה לקריאה — העותק אם כבר נוצר, אחרת המקורית. */
    read(): Uint16Array;
    /** מבטיח עותק שניתן לכתוב אליו, ומחזיר אותו. */
    write(): Uint16Array;
}

/**
 * מקדם קוצר אחד. מחזיר את האשראי שהתווסף, אם היה פריקה.
 */
export function stepHarvester(
    unit: CombatUnit,
    access: OreAccess,
    buildings: readonly Building[],
    grid: Grid,
    pathQueue: UnitId[],
): HarvestOutcome {
    const def = unitDef(unit.type);
    let credits = 0;
    let oreChanged = false;

    const enqueue = (goalTile: number, mode: (typeof UnitMode)[keyof typeof UnitMode]) => {
        unit.goalTile = goalTile;
        unit.mode = mode;
        unit.path = [];
        if (!pathQueue.includes(unit.id)) pathQueue.push(unit.id);
    };

    /*
     * ---- חוזרים לפרוק
     *
     * **`Returning` ולא רק "מלא".** קוצר חלקי שנשלח הביתה כי נגמרו העפרות בטווח
     * הגיע למפעל, עמד לידו לנצח, והאשראי מעולם לא נזקף — הפריקה ישבה אך ורק
     * בענף "מלא". גרוע מזה: הוא נפל בכל טיק לענף חיפוש השדה, סרק את כל המפה,
     * מחק את המסלול שלו ורשם בקשת A* חדשה — שריפה של תקציב הניווט של כולם.
     */
    if (unit.cargo >= def.capacity || (unit.mode === UnitMode.Returning && unit.cargo > 0)) {
        const refinery = nearestRefinery(unit, buildings);
        if (!refinery) return { credits, oreChanged };

        if (atBuilding(unit, refinery)) {
            credits = unit.cargo * ORE_VALUE;
            unit.cargo = 0;
            unit.mode = UnitMode.Idle;
            unit.path = [];
            unit.goalTile = -1;
            return { credits, oreChanged };
        }

        if (unit.mode !== UnitMode.Returning || unit.path.length === 0) {
            const center = buildingCenter(refinery);
            enqueue(
                Math.floor(center.y / TILE) * grid.w + Math.floor(center.x / TILE),
                UnitMode.Returning,
            );
        }
        return { credits, oreChanged };
    }

    // ---- עומדים על עפרות: כורים
    const here = tileOf(unit, grid);
    const available = access.read()[here] ?? 0;
    if (available > 0) {
        const taken = Math.min(def.harvestRate, available, def.capacity - unit.cargo);
        // רק כאן נוצר העותק — זו הכתיבה היחידה לשכבה בכל הסימולציה.
        access.write()[here] = available - taken;
        unit.cargo += taken;
        oreChanged = true;
        // עוצרים במקום כדי לכרות — לא ממשיכים לנסוע תוך כדי.
        unit.path = [];
        unit.goalTile = -1;
        unit.mode = UnitMode.Harvesting;
        return { credits, oreChanged };
    }

    /*
     * ---- מחפשים שדה
     *
     * מדלגים כשכבר יש מסלול פעיל — בין אם לשדה ובין אם הביתה. בלי ה-`Returning`
     * כאן, קוצר בדרך הביתה היה מחשב מחדש בכל טיק ומוחק את המסלול שרק קיבל.
     */
    const busy =
        (unit.mode === UnitMode.Harvesting || unit.mode === UnitMode.Returning) &&
        unit.path.length > 0;
    if (!busy) {
        const tile = nearestOreTile(unit, access.read(), grid);
        if (tile >= 0) enqueue(tile, UnitMode.Harvesting);
        else if (unit.cargo > 0) {
            // אין יותר עפרות בטווח — לפחות מביאים הביתה את מה שיש.
            const refinery = nearestRefinery(unit, buildings);
            if (refinery) {
                const center = buildingCenter(refinery);
                enqueue(
                    Math.floor(center.y / TILE) * grid.w + Math.floor(center.x / TILE),
                    UnitMode.Returning,
                );
            }
        }
    }

    return { credits, oreChanged };
}
